import React from 'react';

import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';

import type { ParityChainSummary } from '~/types/services/priceParrot';

import ScoreDonut from '../PriceParrotParityTool/ScoreDonut';
import ScorePill from '../PriceParrotParityTool/ScorePill';
import { CARD_SX } from '../PriceParrotParityTool/constants';
import { scoreToRating } from '../PriceParrotParityTool/utils';

import StatTile from './StatTile';
import { fmtAud, fmtInt, fmtPct, fmtSignedPct } from './utils';

interface Props {
  summary: ParityChainSummary | null;
  rangeLabel?: string;
  isLoading: boolean;
}

export default function SummaryTiles({ summary, rangeLabel, isLoading }: Props) {
  if (isLoading) {
    return (
      <Box sx={{ ...CARD_SX, p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ ...CARD_SX, p: 4 }}>
        <Typography color="text.secondary">No data for the selected range.</Typography>
      </Box>
    );
  }

  const otaCount = summary.major_competitors.length;

  const pctOop = summary.pct_out_of_parity ?? 0;
  const parityScore = Math.max(0, Math.min(100, Math.round((1 - pctOop) * 100)));
  const rating = scoreToRating(parityScore);
  const alertText = `${rating}: ${fmtPct(pctOop)} of scrapes out of parity (${summary.weighting}-weighted)`;

  const overpricedJobs = summary.jobs_out_of_parity;
  const totalJobs = summary.total_jobs;
  const overpricedPct = totalJobs > 0 ? overpricedJobs / totalJobs : 0;
  const lpcImpressions = summary.lpc_rate_impressions;
  const lpcShareOfTotal = totalJobs > 0 ? lpcImpressions / totalJobs : 0;

  const predictedLiftPct = summary.predicted_lift_pct;
  const showPredictedLift = predictedLiftPct != null && Number.isFinite(predictedLiftPct) && predictedLiftPct > 0;

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 320px) 1fr' },
          gap: 3,
          alignItems: 'stretch',
          mb: 3,
        }}
      >
        <ScorePill
          ring={<ScoreDonut score={parityScore} label="Parity score" />}
          rating={rating}
          alertText={alertText}
        />

        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Full chain: <strong>{summary.property_count}</strong> properties
            {otaCount > 0 && (
              <>
                {' · '}
                <strong>{otaCount}</strong> {otaCount === 1 ? 'OTA' : 'OTAs'}
              </>
            )}
            {' · Weighting: '}
            <strong>{summary.weighting}</strong>
            {rangeLabel && (
              <>
                {' · '}
                <strong>{rangeLabel}</strong>
              </>
            )}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }} useFlexGap>
            <StatTile
              label="Out of parity"
              value={fmtPct(summary.pct_out_of_parity)}
              caption={`${fmtInt(overpricedJobs)} / ${fmtInt(totalJobs)} scrapes (${summary.weighting})`}
            />
            <StatTile
              label="Severe (>10% gap)"
              value={fmtPct(summary.pct_severe)}
              caption={`${fmtInt(summary.jobs_severe)} scrapes (impression-weighted)`}
            />
            <StatTile
              label="Avg price gap"
              value={fmtSignedPct(summary.avg_price_diff)}
              caption={`Under ${summary.weighting} weighting`}
            />
          </Stack>
        </Stack>
      </Box>

      {showPredictedLift && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 14 }}>
            Fixing parity across this chain could lift bookings by ~<strong>{fmtPct(predictedLiftPct)}</strong> vs. the
            current baseline (predicted from observed conversion on in-parity vs. overpriced scrapes).
          </Typography>
        </Alert>
      )}

      <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>Chain activity (selected range)</Typography>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }} useFlexGap>
        <StatTile
          label="Parity checks"
          value={fmtInt(totalJobs)}
          caption={`Across ${summary.property_count} properties`}
        />
        <StatTile
          label="Shown overpriced"
          value={fmtPct(overpricedPct)}
          caption={`${fmtInt(overpricedJobs)} scrapes`}
        />
        <StatTile label="Orders" value={fmtInt(summary.orders)} caption="Bookings attributed to chain" />
        <StatTile label="Revenue" value={fmtAud(summary.revenue_aud)} caption="AUD, selected range" />
      </Stack>

      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2, mt: 2 }} useFlexGap>
        <StatTile
          label="LPC : LPP impressions"
          value={`${fmtInt(summary.lpc_rate_impressions)} : ${fmtInt(summary.lpp_rate_impressions)}`}
          caption={`LPC share ${fmtPct(lpcShareOfTotal)}`}
        />
        <StatTile label="Margin" value={fmtAud(summary.margin_aud)} caption="AUD, selected range" />
        <StatTile label="Currencies" value={fmtInt(summary.currency_count)} caption="Distinct selling currencies" />
      </Stack>
    </Box>
  );
}
